import React from 'react';
import styled from 'styled-components';
import ContactForm from '../components/home/ContactForm';

// Contact Form SVG Icon
const ContactIllustration = () => (
    <svg width="100%" height="100%" viewBox="0 0 500 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="250" cy="200" rx="200" ry="150" fill="#EEF2FF" />

        {/* Envelope */}
        <rect x="150" y="120" width="200" height="150" rx="10" fill="white" stroke="#4338CA" strokeWidth="3" />
        <path d="M150 130L250 200L350 130" stroke="#4338CA" strokeWidth="3" />
        <path d="M150 270L200 220M350 270L300 220" stroke="#4338CA" strokeWidth="3" />

        {/* Messages */}
        <rect x="160" y="90" width="120" height="40" rx="20" fill="#A5B4FC" />
        <rect x="170" y="105" width="100" height="10" rx="5" fill="white" />

        <rect x="180" y="50" width="140" height="40" rx="20" fill="#A5B4FC" />
        <rect x="190" y="65" width="120" height="10" rx="5" fill="white" />

        <rect x="220" y="300" width="130" height="40" rx="20" fill="#A5B4FC" />
        <rect x="230" y="315" width="110" height="10" rx="5" fill="white" />

        {/* Decorations */}
        <circle cx="100" cy="200" r="10" fill="#4338CA" fillOpacity="0.3" />
        <circle cx="400" cy="150" r="15" fill="#4338CA" fillOpacity="0.3" />
        <circle cx="350" cy="80" r="8" fill="#4338CA" fillOpacity="0.3" />
        <circle cx="120" cy="300" r="12" fill="#4338CA" fillOpacity="0.3" />
        <circle cx="380" cy="320" r="9" fill="#4338CA" fillOpacity="0.3" />

        {/* Abstract shapes */}
        <path d="M80 120C90 110 110 130 120 120C130 110 150 130 160 120" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" />
        <path d="M340 250C350 240 370 260 380 250C390 240 410 260 420 250" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

// Styled Components
const PageWrapper = styled.div`
  padding-top: 80px; 
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #4338CA 0%, #7C3AED 100%);
  color: white;
  padding: 100px 0 80px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const HeroContent = styled.div`
  @media (max-width: 992px) {
    text-align: center;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ContactOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2.5rem;
  
  @media (max-width: 992px) {
    align-items: center;
  }
`;

const ContactOption = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const OptionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const OptionText = styled.p`
  font-size: 0.95rem;
  opacity: 0.8;
`;

const HeroImage = styled.div`
  @media (max-width: 992px) {
    display: none;
  }
`;

const OfficeSection = styled.section`
  padding: 100px 0;
  background-color: var(--bg-white);
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const OfficeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const OfficeCard = styled.div`
  background-color: var(--bg-white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-md);
  transition: all var(--transition-normal);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: var(--shadow-lg);
  }
`;

const OfficeImage = styled.div`
  height: 200px;
  background-color: var(--bg-light);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const OfficeContent = styled.div`
  padding: 1.5rem;
`;

const OfficeLocation = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 0.75rem;
`;

const OfficeInfo = styled.p`
  color: var(--text-light);
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
  display: flex;
  align-items: flex-start;
  
  svg {
    margin-right: 0.5rem;
    flex-shrink: 0;
    margin-top: 0.2rem;
  }
  
  a {
    color: var(--primary);
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const OfficeMapImage = () => (
    <svg width="100%" height="100%" viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="200" fill="#E5E7EB" />
        <path d="M0 0L400 200M400 0L0 200" stroke="#D1D5DB" strokeWidth="1" />
        <circle cx="200" cy="100" r="30" fill="#4338CA" fillOpacity="0.3" stroke="#4338CA" strokeWidth="2" />
        <circle cx="200" cy="100" r="5" fill="#4338CA" />
        <rect x="180" y="120" width="40" height="16" rx="2" fill="white" stroke="#4338CA" strokeWidth="1" />
        <text x="200" y="132" fontSize="10" fill="#4338CA" textAnchor="middle" dominantBaseline="middle">地標</text>
        <path d="M50 80 L100 60 L150 70 L200 50 L250 65 L300 40 L350 60" stroke="#9CA3AF" strokeWidth="1" fill="none" />
        <path d="M50 140 L100 130 L150 150 L200 135 L250 145 L300 120 L350 140" stroke="#9CA3AF" strokeWidth="1" fill="none" />
    </svg>
);

// SVG Icons
const PhoneIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4741 21.8325 20.7293C21.7209 20.9845 21.5573 21.2136 21.3521 21.4019C21.1469 21.5901 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77383 17.3147 6.72534 15.2662 5.19 12.85C3.49998 10.2412 2.44824 7.27097 2.12 4.18C2.09501 3.90347 2.12788 3.62476 2.2165 3.36162C2.30513 3.09849 2.44763 2.85669 2.63482 2.65172C2.82202 2.44675 3.04988 2.28281 3.30379 2.17062C3.55769 2.05843 3.83199 2.00036 4.11 2H7.11C7.59531 1.99522 8.06579 2.16708 8.43376 2.48353C8.80173 2.79999 9.04208 3.23945 9.11 3.72C9.23662 4.68007 9.47145 5.62273 9.81 6.53C9.94455 6.88792 9.97366 7.27691 9.89391 7.65088C9.81415 8.02485 9.62886 8.36811 9.36 8.64L8.09 9.91C9.51356 12.4135 11.5865 14.4864 14.09 15.91L15.36 14.64C15.6319 14.3711 15.9752 14.1858 16.3491 14.1061C16.7231 14.0263 17.1121 14.0554 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const EmailIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 6L12 13L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChatIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const LocationIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ContactPage Component
const ContactPage = () => {
    // Office locations data
    const offices = [
        {
            id: 1,
            location: '台北總部',
            address: '新竹市東區中華路二段736號8樓之6',
            phone: '0988-261-474',
            email: 'taipei@shopwave.com.tw'
        },
        {
            id: 2,
            location: '台中辦公室',
            address: '台中市西屯區台灣大道三段658號',
            phone: '04-2345-6789',
            email: 'taichung@shopwave.com.tw'
        },
        {
            id: 3,
            location: '高雄辦公室',
            address: '高雄市前鎮區中華五路789號',
            phone: '07-2345-6789',
            email: 'kaohsiung@shopwave.com.tw'
        }
    ];

    return (
        <PageWrapper>
            <HeroSection>
                <Container>
                    <ContentGrid>
                        <HeroContent>
                            <HeroTitle>與我們聯繫</HeroTitle>
                            <HeroSubtitle>
                                無論您是想了解更多關於我們的服務，或是有任何問題想諮詢，我們的團隊隨時準備為您提供協助。
                            </HeroSubtitle>

                            <ContactOptions>
                                <ContactOption>
                                    <IconWrapper>
                                        <PhoneIcon />
                                    </IconWrapper>
                                    <div>
                                        <OptionTitle>電話聯繫</OptionTitle>
                                        <OptionText>0988-261-474（週一至週五 9:00-18:00）</OptionText>
                                    </div>
                                </ContactOption>

                                <ContactOption>
                                    <IconWrapper>
                                        <EmailIcon />
                                    </IconWrapper>
                                    <div>
                                        <OptionTitle>電子郵件</OptionTitle>
                                        <OptionText>pinter.tw@gmail.com</OptionText>
                                    </div>
                                </ContactOption>

                                <ContactOption>
                                    <IconWrapper>
                                        <ChatIcon />
                                    </IconWrapper>
                                    <div>
                                        <OptionTitle>即時客服</OptionTitle>
                                        <OptionText>使用網站右下角的聊天功能與我們線上交流</OptionText>
                                    </div>
                                </ContactOption>
                            </ContactOptions>
                        </HeroContent>

                        <HeroImage>
                            <ContactIllustration />
                        </HeroImage>
                    </ContentGrid>
                </Container>
            </HeroSection>

            <ContactForm />
        </PageWrapper>
    );
};

export default ContactPage;